// algolia-insights.js
import { useEffect, useMemo, useState } from "react"

import { getCookieValue } from "@utils/google.helpers"
import aa from "search-insights"


/**
 * Consent gate in front of Algolia Insights.
 *
 * Algolia can only report Click-through Rate, Conversion Rate and No Clicks
 * Rate for searches it *tracked* - searches made with `clickAnalytics` on, so
 * that Algolia returned a queryID the later click can be attributed to.
 * Enabling insights on `<InstantSearch>` is what turns that on.
 *
 * Tracking needs a `userToken`, which is a pseudonymous identifier, so it is
 * gated on CookieYes analytics consent. CookieYes is injected from inside GTM
 * (container GTM-P7Z59NC) rather than from this repo, so `window.getCkyConsent`
 * and the `cookieyes-analytics` cookie are runtime globals, not imports.
 */

export const CONSENT_UPDATED_EVENT = "cookieyes_consent_update"

/**
 * Whether the visitor has accepted analytics cookies.
 *
 * Prefers the CookieYes JS API and falls back to the cookie it writes, because
 * the API only exists once the CookieYes script has loaded via GTM - the
 * cookie is readable before that on a repeat visit.
 *
 * @returns {boolean}
 */
export const hasAnalyticsConsent = () => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return false
  }

  try {
    if (typeof window.getCkyConsent === "function") {
      return Boolean(window.getCkyConsent()?.categories?.analytics)
    }
  } catch (err) {
    // A malformed consent object should never take the search page down.
  }

  return getCookieValue("cookieyes-analytics") === "yes"
}

/**
 * The GA4 client id, so Algolia and GA4 identify a visitor the same way and
 * the two datasets stay joinable. Same expression checkout already uses to
 * attach a client id to orders.
 *
 * @returns {string|null}
 */
export const getGaClientId = () => {
  if (typeof document === "undefined") {
    return null
  }

  const ga = getCookieValue("_ga")

  if (!ga) {
    return null
  }

  return ga.split(".").slice(-2).join(".") || null
}

/**
 * Characters Algolia's Insights API accepts in a `userToken`. Anything else
 * fails the whole event with HTTP 422 - "UserToken must contain only
 * alphanumeric, equal, plus, slash, hyphen, or underscore characters, and be
 * between 1 and 129 characters long" - and the event is forwarded to zero
 * destinations, so it reaches neither Analytics nor Personalization.
 *
 * A GA4 client id is two numbers joined by a dot, and a dot is not in that
 * set, so passing it through untouched silently drops every event we send.
 */
const ALGOLIA_TOKEN_DISALLOWED = /[^A-Za-z0-9=+/_-]/g
const ALGOLIA_TOKEN_MAX_LENGTH = 129

/**
 * Rewrites an identifier into something Algolia will accept as a `userToken`.
 *
 * The substitute is a hyphen rather than a deletion so the mapping stays
 * reversible: a GA4 client id is only ever `digits.digits`, so swapping the
 * one disallowed character back turns the Algolia token into the GA4 client
 * id again and the two datasets remain joinable.
 *
 * @param {string|null} value
 * @returns {string|null}
 */
export const toAlgoliaUserToken = (value) => {
  if (!value) {
    return null
  }

  const token = String(value)
    .replace(ALGOLIA_TOKEN_DISALLOWED, "-")
    .slice(0, ALGOLIA_TOKEN_MAX_LENGTH)

  return token || null
}

/**
 * Props for `<InstantSearch insights={...}>`, or `false` to keep Insights off.
 *
 * @returns {false|{insightsClient: Function, insightsInitParams: Object}}
 */
export const buildInsightsProps = () => {
  if (!hasAnalyticsConsent()) {
    return false
  }

  const userToken = toAlgoliaUserToken(getGaClientId())

  return {
    insightsClient: aa,
    insightsInitParams: {
      // The token comes from `_ga`, so search-insights has no reason to
      // persist an `_ALGOLIA` cookie of its own - one identifier, one
      // consent story.
      useCookie: false,
      ...(userToken ? { userToken } : {}),
    },
  }
}

/**
 * Collapses the inputs to `buildInsightsProps` into a comparable primitive, so
 * a consent event that changes nothing does not hand `<InstantSearch>` a new
 * `insights` object and tear its middleware down for no reason.
 *
 * @returns {string}
 */
const readSignature = () =>
  `${hasAnalyticsConsent()}:${toAlgoliaUserToken(getGaClientId()) || ""}`

/**
 * `insights` prop for `<InstantSearch>`, kept in step with consent.
 *
 * CookieYes loads asynchronously through GTM and the visitor may answer the
 * banner long after hydration, so this re-checks on mount and on every
 * `cookieyes_consent_update` - accepting starts tracking without a reload, and
 * withdrawing stops it.
 *
 * @returns {false|{insightsClient: Function, insightsInitParams: Object}}
 */
export const useAlgoliaInsights = () => {
  const [signature, setSignature] = useState(readSignature)

  useEffect(() => {
    const sync = () => setSignature(readSignature())

    // CookieYes may have landed between the first render and this effect.
    sync()

    document.addEventListener(CONSENT_UPDATED_EVENT, sync)
    window.addEventListener(CONSENT_UPDATED_EVENT, sync)

    return () => {
      document.removeEventListener(CONSENT_UPDATED_EVENT, sync)
      window.removeEventListener(CONSENT_UPDATED_EVENT, sync)
    }
  }, [])

  return useMemo(buildInsightsProps, [signature])
}
